import {
  AMAP_CHARGING_VEHICLE_START,
  formatStationDistance,
  type GeoPoint
} from "./amapChargingDemo";
import { getApproxDistanceM } from "./amapParcelDemo";

export type CarwashShopSource = "loading" | "amap" | "demo";

export interface CarwashShop {
  id: string;
  name: string;
  shortName: string;
  address: string;
  location: GeoPoint;
  distanceM: number;
  priceLabel: string;
  couponLabel: string;
  waitMinutes: number;
  routeMinutes: number;
  routeDistanceKm: number;
  detourKm: number;
  ratingLabel: string;
  tag?: string;
  source: Exclude<CarwashShopSource, "loading">;
}

const FALLBACK_CARWASH_TEMPLATES = [
  {
    id: "quick-carwash-riverside",
    name: "云锦路精洗中心",
    shortName: "云锦路精洗",
    address: "当前位置东侧服务区",
    offsetLng: 0.0052,
    offsetLat: 0.0015,
    priceLabel: "29.9元",
    couponLabel: "支付宝券可用",
    waitMinutes: 6,
    detourKm: 0.5,
    ratingLabel: "4.8分",
    tag: "推荐"
  },
  {
    id: "auto-beauty-park",
    name: "车享家汽车美容",
    shortName: "车享家",
    address: "主路北侧商服停车场",
    offsetLng: -0.0038,
    offsetLat: 0.003,
    priceLabel: "35元",
    couponLabel: "可抵10元",
    waitMinutes: 9,
    detourKm: 0.8,
    ratingLabel: "4.7分",
    tag: undefined
  },
  {
    id: "light-carwash-bay",
    name: "小桔自助洗车",
    shortName: "小桔洗车",
    address: "回家路线旁自助洗车点",
    offsetLng: 0.0018,
    offsetLat: -0.0036,
    priceLabel: "19.9元",
    couponLabel: "无需排队",
    waitMinutes: 3,
    detourKm: 1,
    ratingLabel: "4.6分",
    tag: undefined
  }
] as const;

export function createFallbackCarwashShops(
  vehicleLocation: GeoPoint = AMAP_CHARGING_VEHICLE_START.location
): CarwashShop[] {
  return rankCarwashShops(
    FALLBACK_CARWASH_TEMPLATES.map((template) => {
      const location = {
        lng: vehicleLocation.lng + template.offsetLng,
        lat: vehicleLocation.lat + template.offsetLat
      };
      const distanceM = getApproxDistanceM(vehicleLocation, location);
      const routeDistanceKm = Math.max(0.6, distanceM / 1000 * 1.22);
      const routeMinutes = estimateCarwashRouteMinutes(routeDistanceKm);

      return {
        id: template.id,
        name: template.name,
        shortName: template.shortName,
        address: template.address,
        location,
        distanceM,
        priceLabel: template.priceLabel,
        couponLabel: template.couponLabel,
        waitMinutes: template.waitMinutes,
        routeMinutes,
        routeDistanceKm,
        detourKm: template.detourKm,
        ratingLabel: template.ratingLabel,
        tag: template.tag,
        source: "demo" as const
      };
    })
  );
}

export function formatCarwashDistance(distanceM: number) {
  return formatStationDistance(distanceM);
}

export function getCarwashDisplayName(shop: CarwashShop) {
  return shop.shortName || shop.name;
}

export function getRecommendedCarwashShop(shops: CarwashShop[]) {
  return shops.find((shop) => shop.tag === "推荐") ?? shops[0] ?? createFallbackCarwashShops()[0];
}

export function rankCarwashShops(shops: CarwashShop[]) {
  return [...shops]
    .sort((a, b) => getCarwashScore(b) - getCarwashScore(a) || a.distanceM - b.distanceM)
    .map((shop, index) => ({
      ...shop,
      tag: index === 0 ? "推荐" : undefined
    }));
}

export function estimateCarwashRouteMinutes(routeDistanceKm: number) {
  return Math.max(5, Math.round(routeDistanceKm / 20 * 60));
}

function getCarwashScore(shop: CarwashShop) {
  const distanceScore = Math.max(0, 2200 - shop.distanceM) / 2200 * 38;
  const waitScore = Math.max(0, 18 - shop.waitMinutes) * 2.8;
  const couponScore = /券|抵|优惠|无需/.test(shop.couponLabel) ? 18 : 8;
  const detourScore = Math.max(0, 1.8 - shop.detourKm) * 12;

  return distanceScore + waitScore + couponScore + detourScore;
}
